import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { CheckCircle2, Lightbulb } from "lucide-react";
import { LANGS, T } from "../components/tutorial/content";
import { LotCalculator } from "../components/tutorial/LotCalculator";

const IDS = ["rules", "orders", "lot", "tips"];

function Section({ id, title, children }) {
  return (
    <section id={id} className="scroll-mt-28 py-8 border-b border-[color:var(--border-subtle)]" data-testid={`tut-${id}`}>
      <h2 className="font-head text-2xl sm:text-3xl font-bold text-white mb-5">{title}</h2>
      {children}
    </section>
  );
}

export default function Tutorial() {
  const [lang, setLang] = useState(() => localStorage.getItem("tut_lang") || "hng");
  const t = T[lang];
  useEffect(() => { localStorage.setItem("tut_lang", lang); document.title = `${t.title} | Fateh FX`; }, [lang, t.title]);
  return (
    <div dir={lang === "ar" ? "rtl" : "ltr"} lang={lang === "hng" ? "en" : lang} className={`min-h-screen text-white ${lang === "ar" ? "tut-ar" : ""}`} style={{ background: "#0A0A0B" }} data-testid="tutorial-page">
      <header className="sticky top-0 z-30 glass">
        <div className="max-w-3xl mx-auto px-5 pt-3 pb-2 flex items-center gap-2">
          <Link to="/join" className="flex items-center gap-2"><div className="w-8 h-8 flex items-center justify-center bg-gold text-black font-head font-bold text-sm">F</div><span className="font-head font-bold hidden sm:inline">Fateh FX</span></Link>
          <div className="ms-auto flex gap-1" data-testid="tut-lang-switch">
            {LANGS.map((l) => (
              <button key={l.k} data-testid={`tut-lang-${l.k}`} onClick={() => setLang(l.k)}
                className={`px-2.5 py-1.5 text-xs border transition-colors duration-200 ${lang === l.k ? "bg-gold text-black border-gold font-semibold" : "border-[color:var(--border-strong)] text-slate-300"}`}>{l.l}</button>
            ))}
          </div>
        </div>
        <nav className="max-w-3xl mx-auto px-3 flex overflow-x-auto">
          {t.nav.map((n, i) => <a key={n} href={`#${IDS[i]}`} data-testid={`tut-nav-${IDS[i]}`} className="px-2 py-2 text-xs text-slate-400 hover:text-gold whitespace-nowrap">{n}</a>)}
        </nav>
      </header>
      <main className="max-w-3xl mx-auto px-5 pb-16">
        <div className="pt-8 pb-4">
          <h1 className="font-head text-3xl sm:text-4xl font-bold tracking-tight">{t.title}</h1>
          <p className="text-slate-400 mt-2">{t.sub}</p>
        </div>
        <Section id="rules" title={t.nav[0]}>
          <div className="space-y-4">
            {t.rules.map((r) => (
              <div key={r.h} className="surface p-5">
                <h3 className="font-head text-lg text-gold mb-3">{r.h}</h3>
                <ul className="space-y-2">{r.items.map((it) => <li key={it} className="flex gap-2.5 text-sm text-slate-200 leading-relaxed"><CheckCircle2 size={16} className="text-gold shrink-0 mt-0.5" />{it}</li>)}</ul>
              </div>
            ))}
          </div>
        </Section>
        <Section id="orders" title={t.otH}>
          <div className="grid sm:grid-cols-2 gap-3">
            {t.ot.map((o) => (
              <div key={o.n} className="surface p-4"><div className="font-mono text-sm text-gold" dir="ltr">{o.n}</div><p className="text-sm text-slate-300 mt-1.5 leading-relaxed">{o.d}</p></div>
            ))}
          </div>
          <div className="mt-4 flex gap-2.5 text-sm text-slate-200 border border-gold/40 bg-gold/5 p-4"><Lightbulb size={17} className="text-gold shrink-0 mt-0.5" />{t.otTip}</div>
        </Section>
        <Section id="lot" title={t.nav[2]}><LotCalculator t={t} /></Section>
        <Section id="tips" title={t.tipsH}>
          <ul className="space-y-2.5">{t.tips.map((it) => <li key={it} className="flex gap-2.5 text-sm text-slate-200 leading-relaxed"><Lightbulb size={15} className="text-gold shrink-0 mt-0.5" />{it}</li>)}</ul>
        </Section>
        <p className="pt-8 font-head text-gold text-center">{t.motto}</p>
        <p className="pt-4 text-[11px] text-slate-500 leading-relaxed">{t.disc}</p>
      </main>
    </div>
  );
}
